import React, { useState, useEffect, useRef } from "react";
import "./adminLegal.scss";
import {
  useGetLegalDocumentQuery,
  useUpdateLegalDocumentMutation,
} from "../../features/api/legalApiSlice";
import AdminHeader from "../layout/AdminHeader";
import { useToast } from "../toast/ToastContext";

export default function AdminLegalManager() {
  const [selectedSlug, setSelectedSlug] = useState("privacy");
  const [docTitle, setDocTitle] = useState("");
  const [lastUpdated, setLastUpdated] = useState("June 2026");
  const [sections, setSections] = useState([]);

  // 🟢 NEW: UN-BLUE INTERACTIVE STATE TRACKERS
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { showToast } = useToast();
  const { data: fetchedData, isSuccess } =
    useGetLegalDocumentQuery(selectedSlug);
  const [updateLegalDocument, { isLoading: isSaving }] =
    useUpdateLegalDocumentMutation();

  // Close the custom selector if the user clicks anywhere outside the overlay tray boundary
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (isSuccess && fetchedData) {
      setDocTitle(fetchedData.title);
      setLastUpdated(fetchedData.lastUpdated);
      setSections(fetchedData.sections || []);
    } else {
      setDocTitle(
        selectedSlug === "privacy"
          ? "Privacy Policy"
          : selectedSlug === "terms"
          ? "Terms & Conditions"
          : "Safety Guidelines"
      );
      setLastUpdated("June 2026");
      setSections([{ heading: "Initial Heading Block", paragraphs: [""] }]);
    }
  }, [fetchedData, isSuccess, selectedSlug]);

  const handleHeadingChange = (index, value) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], heading: value };
    setSections(updated);
  };

  const handleParagraphChange = (secIdx, pIdx, value) => {
    const updated = [...sections];
    const updatedParagraphs = [...updated[secIdx].paragraphs];
    updatedParagraphs[pIdx] = value;
    updated[secIdx] = { ...updated[secIdx], paragraphs: updatedParagraphs };
    setSections(updated);
  };

  const addSectionBlock = () => {
    setSections([...sections, { heading: "New Section", paragraphs: [""] }]);
  };

  const addParagraphToSection = (secIdx) => {
    const updated = [...sections];
    updated[secIdx] = {
      ...updated[secIdx],
      paragraphs: [...updated[secIdx].paragraphs, ""],
    };
    setSections(updated);
  };

  const removeSectionBlock = (secIdx) => {
    setSections(sections.filter((_, idx) => idx !== secIdx));
  };

  const removeParagraphFromSection = (secIdx, pIdx) => {
    const updated = [...sections];
    const updatedParagraphs = updated[secIdx].paragraphs.filter(
      (_, idx) => idx !== pIdx
    );
    updated[secIdx] = { ...updated[secIdx], paragraphs: updatedParagraphs };
    setSections(updated);
  };

  const handleCommitSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateLegalDocument({
        slug: selectedSlug,
        title: docTitle,
        lastUpdated,
        sections,
      }).unwrap();
      showToast(
        `Successfully saved ${selectedSlug} to the live database!`,
        "success"
      );
    } catch (err) {
      const errorMsg =
        err.data?.message || err.error || "Failed to save data packages.";
      showToast(`Database write error: ${errorMsg}`, "error");
    }
  };

  const currentSelectionLabel =
    selectedSlug === "privacy"
      ? "Privacy Policy"
      : selectedSlug === "terms"
      ? "Terms & Conditions"
      : "Safety Guidelines";

  return (
    <div className="admin-legal-manager">
      {/* 🟢 CUSTOM REUSABLE CENTRAL HEADER */}
      <AdminHeader
        title="Compliance & Legal Document Engine"
        description="Update privacy structures, revise terms of service requirements, or customize driving safety notifications mapped across site nodes."
        badgeText="Legal Logs Matrix"
      />

      {/* 🟢 COMPLIANT UN-BLUE CUSTOM REACT SELECT INTERFACE */}
      <div className="control-selector-row">
        <label>Select Document Target Track:</label>
        <div className="mini-miles-custom-select" ref={dropdownRef}>
          <div
            className={`select-trigger-box ${
              isSelectOpen ? "select-trigger-box--active" : ""
            }`}
            onClick={() => setIsSelectOpen(!isSelectOpen)}
          >
            <span>
              {currentSelectionLabel}{" "}
              {selectedSlug === "privacy" && "(Requires Amazon Clause)"}
            </span>
            <span
              className={`arrow-indicator ${
                isSelectOpen ? "arrow-indicator--rotate" : ""
              }`}
            >
              ▼
            </span>
          </div>

          {isSelectOpen && (
            <ul className="select-options-tray">
              <li
                className={`option-node ${
                  selectedSlug === "privacy" ? "option-node--selected" : ""
                }`}
                onClick={() => {
                  setSelectedSlug("privacy");
                  setIsSelectOpen(false);
                }}
              >
                <span className="type-badge">MANDATORY</span>
                <span className="product-name-text">
                  Privacy Policy (Requires Amazon Clause)
                </span>
              </li>
              <li
                className={`option-node ${
                  selectedSlug === "terms" ? "option-node--selected" : ""
                }`}
                onClick={() => {
                  setSelectedSlug("terms");
                  setIsSelectOpen(false);
                }}
              >
                <span className="type-badge">RULES</span>
                <span className="product-name-text">Terms & Conditions</span>
              </li>
              <li
                className={`option-node ${
                  selectedSlug === "safety" ? "option-node--selected" : ""
                }`}
                onClick={() => {
                  setSelectedSlug("safety");
                  setIsSelectOpen(false);
                }}
              >
                <span className="type-badge">ROUTING</span>
                <span className="product-name-text">Safety Guidelines</span>
              </li>
            </ul>
          )}
        </div>
      </div>

      <form onSubmit={handleCommitSubmit} className="editor-form-chassis">
        <div className="form-group-row">
          <div>
            <label>Document Title</label>
            <input
              type="text"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label>Last Updated Stamp</label>
            <input
              type="text"
              value={lastUpdated}
              onChange={(e) => setLastUpdated(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="sections-build-matrix">
          <h3>Document Structure Layout Sections</h3>
          {sections.map((sec, secIdx) => (
            <fieldset key={secIdx} className="section-fieldset">
              <legend>Section Block 0{secIdx + 1}</legend>

              <div className="field-row">
                <input
                  type="text"
                  value={sec.heading}
                  placeholder="Heading text title..."
                  onChange={(e) => handleHeadingChange(secIdx, e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => removeSectionBlock(secIdx)}
                >
                  Remove Section
                </button>
              </div>

              <div className="paragraphs-nested-block">
                <label>Paragraph Text Matrix Blocks:</label>
                {sec.paragraphs.map((p, pIdx) => (
                  <div key={pIdx} className="paragraph-input-row">
                    <textarea
                      value={p}
                      placeholder="Write policy statement content rules details..."
                      onChange={(e) =>
                        handleParagraphChange(secIdx, pIdx, e.target.value)
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn-delete-small"
                      onClick={() => removeParagraphFromSection(secIdx, pIdx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add-p"
                  onClick={() => addParagraphToSection(secIdx)}
                >
                  + Add Paragraph String
                </button>
              </div>
            </fieldset>
          ))}
        </div>

        <div className="action-button-footer">
          <button
            type="button"
            className="btn-add-section"
            onClick={addSectionBlock}
          >
            + Append New Heading Section
          </button>
          <button type="submit" className="btn-submit-save" disabled={isSaving}>
            {isSaving
              ? "Writing Database Log..."
              : "Commit Update Package to MongoDB"}
          </button>
        </div>
      </form>
    </div>
  );
}
